import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable, inject } from '@angular/core';
import { catchError, EMPTY, map, switchMap, withLatestFrom, of, iif, filter, tap, merge } from 'rxjs';
import { HeatloadService } from '../../heatload/services/heatload.service';
import { EnergySource, HeatGeneratorType, ProjectType } from '@nx-customer-apps/shared/enums';
import { ProjectStore } from '../project/project.store';
import { HeatPumpIntermediaryActions } from './heat-pump-intermediary.actions';
import { ProjectsService } from '../../services';
import { HeatPumpIntermediaryStore } from './heat-pump-intermediary.store';
import { HeatPumpIntermediaryHeatGeneratorDetailsService } from '../../heat-pump-intermediary/services';
import { BuildingType, HeatLoadCalculationMethod } from '@nx-customer-apps/components';

@Injectable()
export class HeatPumpIntermediaryEffects {
    private actions$ = inject(Actions);
    private heatloadService = inject(HeatloadService);
    private projectStore = inject(ProjectStore);
    private projectsService = inject(ProjectsService);
    private heatPumpIntermediaryStore = inject(HeatPumpIntermediaryStore);
    private heatGeneratorDetails = inject(HeatPumpIntermediaryHeatGeneratorDetailsService);

    public onLivingAreaSubmit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HeatPumpIntermediaryActions.submitLivingArea),
            withLatestFrom(this.projectStore.project$, (action, project) => ({ action, project })),
            switchMap(({ project }) => {
                return iif(
                    () =>
                        this.projectsService.isRenovation(project!.projectType as ProjectType) &&
                        this.projectsService.isCalculatedBy(project!, [
                            HeatLoadCalculationMethod.CustomValue,
                            HeatLoadCalculationMethod.Installation,
                            HeatLoadCalculationMethod.RoomByRoom,
                            HeatLoadCalculationMethod.BuildingHull
                        ]),
                    of(HeatPumpIntermediaryActions.getHeatGenerators()),
                    EMPTY
                );
            })
        )
    );

    public onDeviceSubmit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HeatPumpIntermediaryActions.submitDevice),
            withLatestFrom(this.projectStore.project$, (action, project) => ({ action, project })),
            switchMap(({ project }) => {
                return iif(
                    () =>
                        this.projectsService.isRenovation(project!.projectType as ProjectType) &&
                        this.projectsService.isCalculatedBy(project!, HeatLoadCalculationMethod.LivingSpace),
                    of(HeatPumpIntermediaryActions.getHeatGenerators()),
                    EMPTY
                );
            })
        )
    );

    public getHeatGenerators$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HeatPumpIntermediaryActions.getHeatGenerators),
            switchMap(({ selectedEnergySourceType, selectedHeatGeneratorType }) =>
                this.heatloadService.getHeatGenerators().pipe(
                    map(heatGenerators =>
                        HeatPumpIntermediaryActions.getHeatGeneratorsSuccess({
                            heatGenerators,
                            selectedEnergySourceType,
                            selectedHeatGeneratorType
                        })
                    ),
                    catchError(() => EMPTY)
                )
            )
        )
    );

    public onEnergyConsumptionSubmit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HeatPumpIntermediaryActions.submitEnergyConsumption),
            map(() => HeatPumpIntermediaryActions.getConsumptionProfiles())
        )
    );

    public getConsumptionProfiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HeatPumpIntermediaryActions.getConsumptionProfiles),
            withLatestFrom(this.projectStore.project$, (action, project) => ({ action, project })),
            switchMap(({ project }) =>
                this.heatloadService
                    .getConsumptionProfiles(project!.projectType as ProjectType, project!.building.buildingType as BuildingType)
                    .pipe(map(consumptionProfiles => HeatPumpIntermediaryActions.getConsumptionProfilesSuccess({ consumptionProfiles })))
            )
        )
    );

    public getHeatingSystems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HeatPumpIntermediaryActions.getHeatingSystems),
            withLatestFrom(this.heatPumpIntermediaryStore.selectedHeatingEnergySource$, (action, energySourceState) => ({
                action,
                energySourceState
            })),
            switchMap(({ action, energySourceState }) =>
                this.heatloadService
                    .getHeatingSystems(
                        energySourceState!.selectedEnergySource!.type as EnergySource,
                        +action.installationYear,
                        energySourceState!.selectedHeatGenerator as HeatGeneratorType
                    )
                    .pipe(catchError(error => EMPTY))
            ),
            map(heatingSystems => HeatPumpIntermediaryActions.getHeatingSystemsSuccess({ heatingSystems }))
        )
    );

    /**
     * Reseting the installation year "prefill finished" state to make the prefilling work again when resubmiting the energy source accordion.
     */
    public resetHeatGeneratorDetailsPrefillState$ = createEffect(
        () =>
            merge(
                this.heatPumpIntermediaryStore.heatGeneratorDetails$,
                this.heatPumpIntermediaryStore.selectedHeatingEnergySource$.pipe(filter(Boolean))
            ).pipe(tap(() => this.heatGeneratorDetails.toggleIsConstructionYearPrefillFinished(false))),
        { dispatch: false }
    );
}
